import { GameState } from '../game/GameState';
import type {
  FactionState,
  HolySiteState,
  IdentityHistoryEntry,
  ProvinceCulturePolicy,
  ProvinceReligionPolicy,
  ProvinceState,
  ReligiousMovementState,
} from '../types/game';
import { DataRegistry } from './DataRegistry';

const RELIGION_POLICIES: ProvinceReligionPolicy[] = ['tolerate', 'syncretize', 'convert'];
const CULTURE_POLICIES: ProvinceCulturePolicy[] = ['local_elites', 'integrate', 'assimilate'];

export class IdentityManager {
  static readonly instance = new IdentityManager();

  ensureCampaignInitialized() {
    const campaign = GameState.instance.requireCampaign();
    campaign.holySites ??= {};
    campaign.religiousMovements ??= {};
    campaign.identityHistory ??= [];

    for (const faction of Object.values(campaign.factions)) this.ensureFaction(faction);
    for (const province of Object.values(campaign.provinces)) this.ensureProvince(province);
    this.ensureHolySites();
    this.refreshHolySiteControllers();
  }

  processMonth() {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();

    for (const province of Object.values(campaign.provinces)) this.processProvince(province);
    this.refreshHolySiteControllers();
    this.processHolySites();
    this.processMovements();
    this.processAiPolicies();
  }

  religions() {
    const campaign = GameState.instance.requireCampaign();
    return [...new Set([
      ...Object.values(campaign.factions).map((faction) => faction.stateReligion),
      ...Object.values(campaign.provinces).map((province) => province.religion),
    ])].filter(Boolean).sort();
  }

  cultures() {
    const campaign = GameState.instance.requireCampaign();
    return [...new Set([
      ...Object.values(campaign.factions).map((faction) => faction.primaryCulture),
      ...Object.values(campaign.provinces).map((province) => province.culture),
    ])].filter(Boolean).sort();
  }

  provincesForFaction(factionId: string) {
    return Object.values(GameState.instance.requireCampaign().provinces)
      .filter((province) => province.owner === factionId)
      .sort((a, b) => b.religiousTension + b.culturalTension - (a.religiousTension + a.culturalTension));
  }

  movementsForFaction(factionId: string) {
    return Object.values(GameState.instance.requireCampaign().religiousMovements)
      .filter((movement) => movement.factionId === factionId && movement.status === 'active')
      .sort((a, b) => b.strength * b.zeal - a.strength * a.zeal);
  }

  holySitesForFaction(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction) return [];
    return Object.values(campaign.holySites)
      .filter((site) => site.religion === faction.stateReligion || site.controllerFactionId === factionId || site.patronFactionId === factionId)
      .sort((a, b) => Number(b.patronFactionId === factionId) - Number(a.patronFactionId === factionId) || b.pilgrimageValue - a.pilgrimageValue);
  }

  policySummary(provinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    const owner = province ? campaign.factions[province.owner] : undefined;
    if (!province || !owner) return undefined;
    const religionAccepted = province.religion === owner.stateReligion;
    const cultureAccepted = province.culture === owner.primaryCulture || owner.acceptedCultures.includes(province.culture);
    return {
      province,
      owner,
      religionAccepted,
      cultureAccepted,
      unrestPressure: Math.round((religionAccepted ? 0 : province.religiousTension * 0.25) + (cultureAccepted ? 0 : province.culturalTension * 0.22)),
      conversionMonths: province.religiousPolicy === 'convert' ? Math.max(1, Math.ceil((100 - province.conversionProgress) / Math.max(1, this.conversionRate(province, owner)))) : undefined,
      assimilationMonths: province.culturalPolicy === 'assimilate' ? Math.max(1, Math.ceil((100 - province.assimilationProgress) / Math.max(1, this.assimilationRate(province, owner)))) : undefined,
    };
  }

  setReligionPolicy(factionId: string, provinceId: string, policy: ProvinceReligionPolicy) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    if (!province || province.owner !== factionId || !RELIGION_POLICIES.includes(policy)) return false;
    province.religiousPolicy = policy;
    if (policy === 'tolerate') province.conversionProgress = Math.max(0, province.conversionProgress - 5);
    if (policy === 'convert') province.religiousTension = Math.min(100, province.religiousTension + 4);
    GameState.instance.log(`${DataRegistry.instance.provinces[provinceId]?.name ?? provinceId} adopted a ${policy} religious policy.`, policy === 'convert' ? 'warning' : 'success');
    return true;
  }

  setCulturePolicy(factionId: string, provinceId: string, policy: ProvinceCulturePolicy) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    if (!province || province.owner !== factionId || !CULTURE_POLICIES.includes(policy)) return false;
    province.culturalPolicy = policy;
    if (policy === 'local_elites') {
      province.localEliteInfluence = Math.min(100, province.localEliteInfluence + 6);
      province.assimilationProgress = Math.max(0, province.assimilationProgress - 4);
    }
    if (policy === 'assimilate') province.culturalTension = Math.min(100, province.culturalTension + 4);
    GameState.instance.log(`${DataRegistry.instance.provinces[provinceId]?.name ?? provinceId} adopted a ${policy.replaceAll('_', ' ')} cultural policy.`, policy === 'assimilate' ? 'warning' : 'success');
    return true;
  }

  changeStateReligion(factionId: string, religion: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction || faction.stateReligion === religion || faction.treasury < 180 || faction.legitimacy < 45) return false;
    const oldReligion = faction.stateReligion;
    faction.treasury -= 180;
    faction.legitimacy = Math.max(0, faction.legitimacy - 18);
    faction.prestige = Math.max(0, faction.prestige - 8);
    faction.stateReligion = religion;
    faction.religiousAuthority = 35;
    faction.syncretism = Math.min(100, faction.syncretism + 15);
    for (const province of this.provincesForFaction(factionId)) {
      province.religiousTension = Math.min(100, province.religiousTension + (province.religion === religion ? 4 : 18));
    }
    this.record(factionId, 'tolerance', 'A New State Faith', `${DataRegistry.instance.factions[factionId]?.name ?? factionId} abandoned ${oldReligion} and adopted ${religion}.`);
    GameState.instance.log(`The realm adopted ${religion}. Expect a difficult religious settlement.`, 'danger');
    return true;
  }

  recognizeCulture(factionId: string, culture: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction || culture === faction.primaryCulture || faction.acceptedCultures.includes(culture) || faction.treasury < 90) return false;
    const population = Object.values(campaign.provinces).filter((province) => province.owner === factionId && province.culture === culture).reduce((sum, province) => sum + province.population, 0);
    if (population <= 0) return false;
    faction.treasury -= 90;
    faction.acceptedCultures.push(culture);
    faction.prestige = Math.min(100, faction.prestige + 3);
    for (const province of this.provincesForFaction(factionId).filter((entry) => entry.culture === culture)) {
      province.culturalTension = Math.max(0, province.culturalTension - 25);
      province.loyalty = Math.min(100, province.loyalty + 8);
    }
    this.record(factionId, 'culture_acceptance', `${culture} Recognized`, `${culture} elites were admitted to offices and privileges of the realm.`);
    return true;
  }

  patronizeHolySite(factionId: string, siteId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const site = campaign.holySites[siteId];
    if (!faction || !site || faction.treasury < 65 || (site.controllerFactionId !== factionId && site.religion !== faction.stateReligion)) return false;
    faction.treasury -= 65;
    faction.patronage = Math.min(100, faction.patronage + 8);
    faction.religiousAuthority = Math.min(100, faction.religiousAuthority + 5);
    faction.prestige = Math.min(100, faction.prestige + 2);
    site.patronFactionId = factionId;
    site.sanctity = Math.min(100, site.sanctity + 6);
    site.pilgrimageValue = Math.min(100, site.pilgrimageValue + 4);
    this.record(factionId, 'holy_site', `${site.name} Patronized`, `Royal patronage renewed pilgrimage, scholarship and ceremonial life at ${site.name}.`, site.provinceId);
    return true;
  }

  resolveMovement(factionId: string, movementId: string, approach: 'appease' | 'suppress' | 'recognize') {
    const campaign = GameState.instance.requireCampaign();
    const movement = campaign.religiousMovements[movementId];
    const faction = campaign.factions[factionId];
    if (!movement || movement.factionId !== factionId || movement.status !== 'active' || !faction) return false;

    if (approach === 'appease') {
      const cost = 35 + movement.strength;
      if (faction.treasury < cost) return false;
      faction.treasury -= cost;
      faction.tolerance = Math.min(100, faction.tolerance + 5);
      movement.status = 'appeased';
      for (const provinceId of movement.provinceIds) {
        const province = campaign.provinces[provinceId];
        if (!province) continue;
        province.religiousTension = Math.max(0, province.religiousTension - 22);
        province.unrest = Math.max(0, province.unrest - 8);
      }
    } else if (approach === 'recognize') {
      faction.syncretism = Math.min(100, faction.syncretism + 9);
      faction.religiousAuthority = Math.max(0, faction.religiousAuthority - 5);
      movement.status = 'appeased';
      for (const provinceId of movement.provinceIds) {
        const province = campaign.provinces[provinceId];
        if (!province) continue;
        province.religiousPolicy = 'syncretize';
        province.religiousTension = Math.max(0, province.religiousTension - 30);
      }
    } else {
      const militaryStrength = Object.values(campaign.armies).filter((army) => army.factionId === factionId && movement.provinceIds.includes(army.provinceId)).reduce((sum, army) => sum + army.units.length * 4, 0);
      const success = militaryStrength + faction.legitimacy * 0.35 >= movement.strength + movement.zeal * 0.45;
      if (success) {
        movement.status = 'suppressed';
        faction.prestige = Math.min(100, faction.prestige + 3);
        for (const provinceId of movement.provinceIds) {
          const province = campaign.provinces[provinceId];
          if (!province) continue;
          province.unrest = Math.min(100, province.unrest + 6);
          province.religiousTension = Math.max(0, province.religiousTension - 12);
        }
      } else {
        movement.strength = Math.min(100, movement.strength + 15);
        faction.legitimacy = Math.max(0, faction.legitimacy - 8);
        GameState.instance.log(`Suppression of ${movement.demands} failed and radicalized the movement.`, 'danger');
        return true;
      }
    }
    this.record(factionId, 'movement', 'Religious Movement Resolved', `${movement.demands} was ${movement.status}.`);
    return true;
  }

  private ensureFaction(faction: FactionState) {
    const data = DataRegistry.instance.factions[faction.id];
    faction.stateReligion ??= data?.stateReligion ?? 'Local Traditions';
    faction.primaryCulture ??= data?.primaryCulture ?? 'Cosmopolitan';
    faction.religiousAuthority ??= 55;
    faction.tolerance ??= 42;
    faction.syncretism ??= faction.stateReligion.toLowerCase().includes('syncret') ? 65 : 24;
    faction.acceptedCultures ??= [];
    faction.diplomaticReputation ??= 0;
    faction.patronage ??= 10;
  }

  private ensureProvince(province: ProvinceState) {
    const data = DataRegistry.instance.provinces[province.id];
    province.religion ??= data?.religion ?? 'Local Traditions';
    province.culture ??= data?.culture ?? 'Local';
    province.religiousPolicy ??= 'tolerate';
    province.culturalPolicy ??= 'local_elites';
    province.conversionProgress ??= 0;
    province.assimilationProgress ??= 0;
    province.religiousTension ??= 8;
    province.culturalTension ??= 8;
    province.localEliteInfluence ??= 55;
  }

  private ensureHolySites() {
    const campaign = GameState.instance.requireCampaign();
    if (Object.keys(campaign.holySites).length) return;
    const grouped = new Map<string, ProvinceState[]>();
    for (const province of Object.values(campaign.provinces)) grouped.set(province.religion, [...(grouped.get(province.religion) ?? []), province]);
    for (const [religion, provinces] of grouped) {
      const candidates = provinces.sort((a, b) => this.holySiteScore(b) - this.holySiteScore(a)).slice(0, Math.min(2, provinces.length));
      candidates.forEach((province, index) => {
        const provinceName = DataRegistry.instance.provinces[province.id]?.name ?? province.id;
        const id = `holy_site_${this.slug(religion)}_${province.id}`;
        campaign.holySites[id] = {
          id,
          provinceId: province.id,
          name: index === 0 ? `Great Sanctuary of ${provinceName}` : `Pilgrimage Shrine of ${provinceName}`,
          religion,
          controllerFactionId: province.controller,
          sanctity: index === 0 ? 78 : 60,
          pilgrimageValue: index === 0 ? 70 : 48,
          contested: false,
        };
      });
    }
  }

  private processProvince(province: ProvinceState) {
    const campaign = GameState.instance.requireCampaign();
    const owner = campaign.factions[province.owner];
    if (!owner) return;
    const religionMismatch = province.religion !== owner.stateReligion;
    const cultureAccepted = province.culture === owner.primaryCulture || owner.acceptedCultures.includes(province.culture);

    if (!religionMismatch) {
      province.conversionProgress = 0;
      province.religiousTension = Math.max(0, province.religiousTension - 2);
    } else if (province.religiousPolicy === 'convert') {
      province.conversionProgress = Math.min(100, province.conversionProgress + this.conversionRate(province, owner));
      province.religiousTension = Math.min(100, province.religiousTension + Math.max(0.4, 2.6 - owner.tolerance * 0.018 - owner.syncretism * 0.012));
      if (province.conversionProgress >= 100) this.completeConversion(province, owner);
    } else if (province.religiousPolicy === 'syncretize') {
      province.conversionProgress = Math.min(75, province.conversionProgress + Math.max(0.2, owner.syncretism * 0.018));
      province.religiousTension = Math.max(0, province.religiousTension - 1.4);
      province.loyalty = Math.min(100, province.loyalty + 0.15);
    } else {
      province.conversionProgress = Math.max(0, province.conversionProgress - 0.6);
      province.religiousTension = Math.max(0, province.religiousTension - Math.max(0.4, owner.tolerance * 0.018));
    }

    if (cultureAccepted) {
      province.assimilationProgress = 0;
      province.culturalTension = Math.max(0, province.culturalTension - 1.6);
    } else if (province.culturalPolicy === 'assimilate') {
      province.assimilationProgress = Math.min(100, province.assimilationProgress + this.assimilationRate(province, owner));
      province.culturalTension = Math.min(100, province.culturalTension + Math.max(0.3, 2.2 - province.prosperity * 0.012));
      province.localEliteInfluence = Math.max(0, province.localEliteInfluence - 0.7);
      if (province.assimilationProgress >= 100) this.completeAssimilation(province, owner);
    } else if (province.culturalPolicy === 'integrate') {
      province.assimilationProgress = Math.min(80, province.assimilationProgress + 0.6 + province.prosperity * 0.008);
      province.culturalTension = Math.max(0, province.culturalTension - 0.7);
      province.localEliteInfluence = Math.max(25, province.localEliteInfluence - 0.15);
    } else {
      province.assimilationProgress = Math.max(0, province.assimilationProgress - 0.4);
      province.culturalTension = Math.max(0, province.culturalTension - 0.9);
      province.localEliteInfluence = Math.min(100, province.localEliteInfluence + 0.3);
      province.autonomy = Math.min(100, province.autonomy + 0.08);
    }

    const tensionPressure = (religionMismatch ? province.religiousTension * (1 - owner.tolerance / 150) : 0)
      + (!cultureAccepted ? province.culturalTension * (1 - province.localEliteInfluence / 180) : 0);
    province.unrest = Math.max(0, Math.min(100, province.unrest + tensionPressure * 0.018 - province.loyalty * 0.004));
  }

  private processHolySites() {
    const campaign = GameState.instance.requireCampaign();
    for (const site of Object.values(campaign.holySites)) {
      const controller = site.controllerFactionId ? campaign.factions[site.controllerFactionId] : undefined;
      if (!controller) continue;
      site.contested = campaign.provinces[site.provinceId]?.controller !== campaign.provinces[site.provinceId]?.owner;
      if (controller.stateReligion === site.religion) {
        controller.religiousAuthority = Math.min(100, controller.religiousAuthority + site.sanctity * 0.003);
        controller.prestige = Math.min(100, controller.prestige + site.pilgrimageValue * 0.0015);
        controller.treasury += Math.max(0, Math.round(site.pilgrimageValue / 45));
      } else {
        controller.tolerance = Math.min(100, controller.tolerance + 0.04);
        controller.religiousAuthority = Math.max(0, controller.religiousAuthority - 0.03);
      }
      if (site.patronFactionId) {
        const patron = campaign.factions[site.patronFactionId];
        if (patron) patron.patronage = Math.min(100, patron.patronage + 0.08);
      }
    }
  }

  private processMovements() {
    const campaign = GameState.instance.requireCampaign();
    for (const movement of Object.values(campaign.religiousMovements)) {
      if (movement.status !== 'active') continue;
      const faction = campaign.factions[movement.factionId];
      if (!faction) continue;
      const averageTension = movement.provinceIds.reduce((sum, id) => sum + (campaign.provinces[id]?.religiousTension ?? 0), 0) / Math.max(1, movement.provinceIds.length);
      movement.support = Math.max(0, Math.min(100, movement.support + averageTension * 0.025 - faction.tolerance * 0.008));
      movement.strength = Math.max(0, Math.min(100, movement.strength + movement.support * 0.012 - faction.legitimacy * 0.004));
      if (movement.strength > 82 && movement.support > 72) {
        movement.status = 'victorious';
        faction.legitimacy = Math.max(0, faction.legitimacy - 15);
        for (const provinceId of movement.provinceIds) {
          const province = campaign.provinces[provinceId];
          if (!province) continue;
          province.religion = movement.religion;
          province.religiousTension = 25;
          province.unrest = Math.min(100, province.unrest + 18);
        }
        GameState.instance.log(`${movement.demands} forced concessions after a mass religious uprising.`, 'danger');
      }
    }

    for (const faction of Object.values(campaign.factions)) {
      if (this.movementsForFaction(faction.id).length >= 2) continue;
      const flashpoints = this.provincesForFaction(faction.id).filter((province) => province.religion !== faction.stateReligion && province.religiousTension >= 58);
      if (!flashpoints.length) continue;
      const seed = this.hash(`${campaign.turn}:${faction.id}:movement`);
      if (seed % 100 >= Math.min(35, Math.round(flashpoints.reduce((sum, province) => sum + province.religiousTension, 0) / flashpoints.length / 3))) continue;
      const religion = flashpoints.sort((a, b) => b.religiousTension - a.religiousTension)[0].religion;
      const provinces = flashpoints.filter((province) => province.religion === religion).slice(0, 4);
      const id = `movement_${faction.id}_${this.slug(religion)}_${campaign.turn}`;
      campaign.religiousMovements[id] = {
        id,
        factionId: faction.id,
        religion,
        provinceIds: provinces.map((province) => province.id),
        strength: 28 + seed % 18,
        zeal: 45 + (seed >> 3) % 35,
        support: 35 + (seed >> 5) % 30,
        demands: `${religion} communities demand protection, worship rights and an end to coercive conversion`,
        startedTurn: campaign.turn,
        status: 'active',
      };
      this.record(faction.id, 'movement', `${religion} Movement`, campaign.religiousMovements[id].demands, provinces[0]?.id);
      if (faction.id === campaign.playerFactionId) GameState.instance.log(`A ${religion} religious movement has organized across ${provinces.length} provinces.`, 'danger');
    }
  }

  private processAiPolicies() {
    const campaign = GameState.instance.requireCampaign();
    for (const faction of Object.values(campaign.factions)) {
      if (faction.id === campaign.playerFactionId) continue;
      const data = DataRegistry.instance.factions[faction.id];
      const coercive = ['zealous_expansionist', 'imperial_court', 'colonial_strategist'].includes(data?.aiPersonality ?? '');
      for (const province of this.provincesForFaction(faction.id)) {
        if (province.religion !== faction.stateReligion) province.religiousPolicy = coercive && faction.religiousAuthority > 52 ? 'convert' : faction.syncretism > 45 ? 'syncretize' : 'tolerate';
        if (province.culture !== faction.primaryCulture && !faction.acceptedCultures.includes(province.culture)) {
          province.culturalPolicy = province.culturalTension > 55 ? 'local_elites' : coercive && province.prosperity > 45 ? 'assimilate' : 'integrate';
        }
      }
      const majorCulture = this.provincesForFaction(faction.id)
        .filter((province) => province.culture !== faction.primaryCulture && !faction.acceptedCultures.includes(province.culture))
        .reduce<Record<string, number>>((totals, province) => ({ ...totals, [province.culture]: (totals[province.culture] ?? 0) + province.population }), {});
      const candidate = Object.entries(majorCulture).sort((a, b) => b[1] - a[1])[0];
      if (candidate && candidate[1] > this.provincesForFaction(faction.id).reduce((sum, province) => sum + province.population, 0) * 0.28 && faction.treasury > 180) this.recognizeCulture(faction.id, candidate[0]);
    }
  }

  private refreshHolySiteControllers() {
    const campaign = GameState.instance.requireCampaign();
    for (const site of Object.values(campaign.holySites)) site.controllerFactionId = campaign.provinces[site.provinceId]?.controller;
  }

  private completeConversion(province: ProvinceState, owner: FactionState) {
    const previous = province.religion;
    province.religion = owner.stateReligion;
    province.conversionProgress = 0;
    province.religiousTension = Math.max(12, province.religiousTension - 25);
    province.loyalty = Math.min(100, province.loyalty + 4);
    province.lastIdentityChangeTurn = GameState.instance.requireCampaign().turn;
    owner.religiousAuthority = Math.min(100, owner.religiousAuthority + 1.5);
    this.record(owner.id, 'conversion', `${DataRegistry.instance.provinces[province.id]?.name ?? province.id} Converted`, `${previous} communities gradually adopted ${owner.stateReligion}.`, province.id);
    if (owner.id === GameState.instance.requireCampaign().playerFactionId) GameState.instance.log(`${DataRegistry.instance.provinces[province.id]?.name ?? province.id} converted to ${owner.stateReligion}.`, 'success');
  }

  private completeAssimilation(province: ProvinceState, owner: FactionState) {
    const previous = province.culture;
    province.culture = owner.primaryCulture;
    province.assimilationProgress = 0;
    province.culturalTension = Math.max(10, province.culturalTension - 24);
    province.localEliteInfluence = 45;
    province.loyalty = Math.min(100, province.loyalty + 4);
    province.lastIdentityChangeTurn = GameState.instance.requireCampaign().turn;
    this.record(owner.id, 'assimilation', `${DataRegistry.instance.provinces[province.id]?.name ?? province.id} Integrated`, `${previous} institutions gradually merged into the ${owner.primaryCulture} political culture.`, province.id);
  }

  private conversionRate(province: ProvinceState, owner: FactionState) {
    const clergy = GameState.instance.requireCampaign().courtEstates[`${owner.id}:clergy`];
    const clergySupport = clergy ? (clergy.approval + clergy.influence) / 80 : 1;
    return Math.max(0.35, 0.7 + owner.religiousAuthority * 0.026 + owner.patronage * 0.009 + clergySupport - province.autonomy * 0.012 - province.religiousTension * 0.008);
  }

  private assimilationRate(province: ProvinceState, owner: FactionState) {
    return Math.max(0.3, 0.55 + owner.diplomaticReputation * 0.01 + province.prosperity * 0.018 + province.loyalty * 0.01 - province.autonomy * 0.012 - province.localEliteInfluence * 0.006);
  }

  private holySiteScore(province: ProvinceState) {
    const data = DataRegistry.instance.provinces[province.id];
    return province.population + province.prosperity + (data?.port ? 20 : 0) + province.buildings.length * 4;
  }

  private record(factionId: string, type: IdentityHistoryEntry['type'], title: string, description: string, provinceId?: string) {
    const campaign = GameState.instance.requireCampaign();
    campaign.identityHistory.unshift({ id: `identity_${type}_${campaign.turn}_${campaign.identityHistory.length}`, turn: campaign.turn, factionId, provinceId, type, title, description });
    campaign.identityHistory = campaign.identityHistory.slice(0, 180);
  }

  private hash(value: string) { return [...value].reduce((sum, character, index) => ((sum * 31) + character.charCodeAt(0) + index) >>> 0, 2166136261); }
  private slug(value: string) { return value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''); }
}
